/**
 * FormAnalysisCard - Display form analysis for both teams
 * Phase 13.6 - Form Streaks & Trends
 */
import React from 'react'
import useSWR from 'swr'
import type { FormComparisonOut } from '../types'
import { apiGet } from '../api'
import { StreakBadge, UnbeatenBadge, WinlessBadge } from './StreakBadge'
import { FormTrendIndicator, FormAdvantageIndicator } from './FormTrendIndicator'

interface FormAnalysisCardProps {
  fixtureId: number
  homeTeamName: string
  awayTeamName: string
  homeCrest?: string | null
  awayCrest?: string | null
}

function FormDisplay({ form }: { form: string | null | undefined }) {
  if (!form) return <span className="small">-</span>
  const recent = form.slice(-5).toUpperCase()
  return (
    <div className="form-string-display">
      {recent.split('').map((char, i) => (
        <span
          key={i}
          className={`form-char ${char === 'W' ? 'win' : char === 'D' ? 'draw' : 'loss'}`}
        >
          {char}
        </span>
      ))}
    </div>
  )
}

export function FormAnalysisCard({
  fixtureId,
  homeTeamName,
  awayTeamName,
  homeCrest,
  awayCrest,
}: FormAnalysisCardProps) {
  const { data, error, isLoading } = useSWR<FormComparisonOut>(
    `/fixtures/${fixtureId}/form-analysis`,
    apiGet,
    { revalidateOnFocus: false }
  )

  if (isLoading) {
    return (
      <div className="card form-analysis-card">
        <div className="card-inner">
          <div className="card-header">Form Analysis</div>
          <div className="skeleton" style={{ height: 100 }} />
        </div>
      </div>
    )
  }

  if (error || !data) {
    return null // Silently fail - form analysis is supplementary
  }

  const { home, away, advantage, advantage_score } = data

  return (
    <div className="card form-analysis-card">
      <div className="card-inner">
        <div className="card-header">
          <span>Form Analysis</span>
          {advantage !== 'even' && (
            <FormAdvantageIndicator
              advantage={advantage}
              advantageScore={advantage_score}
              homeName={homeTeamName}
              awayName={awayTeamName}
            />
          )}
        </div>

        <div className="form-analysis-content">
          {/* Home team */}
          <div className="form-analysis-team">
            <div className="form-team-header">
              <span className="badge">
                {homeCrest ? <img src={homeCrest} alt="" /> : 'H'}
              </span>
              <span className="team-name">{homeTeamName}</span>
            </div>
            <div className="form-badges">
              <StreakBadge streak={home.current_streak} />
              <UnbeatenBadge count={home.unbeaten_run} minToShow={4} />
              <WinlessBadge count={home.winless_run} minToShow={4} />
            </div>
            <div className="form-details">
              <FormDisplay form={home.form_string} />
              <FormTrendIndicator trend={home.trend} trendScore={home.trend_score} />
            </div>
            <div className="form-stats">
              <span className="form-stat">
                <span className="form-stat-value">{home.last_5_points}</span>
                <span className="form-stat-label">pts (L5)</span>
              </span>
              <span className="form-stat">
                <span className="form-stat-value">{home.last_5_wins}W</span>
                <span className="form-stat-value">{home.last_5_draws}D</span>
                <span className="form-stat-value">{home.last_5_losses}L</span>
              </span>
            </div>
          </div>

          {/* Divider */}
          <div className="form-divider" />

          {/* Away team */}
          <div className="form-analysis-team">
            <div className="form-team-header">
              <span className="badge">
                {awayCrest ? <img src={awayCrest} alt="" /> : 'A'}
              </span>
              <span className="team-name">{awayTeamName}</span>
            </div>
            <div className="form-badges">
              <StreakBadge streak={away.current_streak} />
              <UnbeatenBadge count={away.unbeaten_run} minToShow={4} />
              <WinlessBadge count={away.winless_run} minToShow={4} />
            </div>
            <div className="form-details">
              <FormDisplay form={away.form_string} />
              <FormTrendIndicator trend={away.trend} trendScore={away.trend_score} />
            </div>
            <div className="form-stats">
              <span className="form-stat">
                <span className="form-stat-value">{away.last_5_points}</span>
                <span className="form-stat-label">pts (L5)</span>
              </span>
              <span className="form-stat">
                <span className="form-stat-value">{away.last_5_wins}W</span>
                <span className="form-stat-value">{away.last_5_draws}D</span>
                <span className="form-stat-value">{away.last_5_losses}L</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
